import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Save } from 'lucide-react'
import { geraete as geraeteApi, kunden as kundenApi } from '../../lib/api'
import { TopBar } from '../../components/layout/TopBar'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Textarea } from '../../components/ui/Textarea'
import { Button } from '../../components/ui/Button'
import { Card } from '../../components/ui/Card'
import { BarcodeScanner } from '../../components/ui/BarcodeScanner'
import { GERAETE_MODELLE } from '../../types'
import type { KundenGeraet } from '../../types'

const schema = z.object({
  kunde_id: z.string().min(1, 'Kunde erforderlich'),
  modell: z.string().min(1, 'Modell erforderlich'),
  seriennummer: z.string().optional(),
  kaufdatum: z.string().optional(),
  garantie_bis: z.string().optional(),
  abo_aktiv: z.string().optional(),
  status: z.string().default('aktiv'),
  standort: z.string().optional(),
  notizen: z.string().optional(),
})

type FormData = z.infer<typeof schema>

export function GeraetFormPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const qc = useQueryClient()

  const { data: kundenList } = useQuery({ queryKey: ['kunden'], queryFn: () => kundenApi.list() })

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      kunde_id: searchParams.get('kunde_id') ?? '',
      modell: 'DS4',
      status: 'aktiv',
    },
  })

  const mutation = useMutation({
    mutationFn: (data: Partial<KundenGeraet>) => geraeteApi.create({ ...data, abo_aktiv: data.abo_aktiv ? 1 : 0 }),
    onSuccess: (g) => {
      qc.invalidateQueries({ queryKey: ['geraete'] })
      navigate(`/inventar/${g.id}`)
    },
  })

  return (
    <>
      <TopBar title="Gerät hinzufügen" backTo="/inventar" />
      <div className="px-4 py-3 pb-8">
        <form onSubmit={handleSubmit((d) => mutation.mutate({
          ...d as unknown as Partial<KundenGeraet>,
          abo_aktiv: d.abo_aktiv === '1' ? 1 : 0
        }))} className="space-y-4">
          <Card>
            <div className="space-y-4">
              <Select
                label="Kunde" required
                options={(kundenList ?? []).map((k) => ({ value: k.id, label: k.firma ?? k.name }))}
                placeholder="Kunde auswählen"
                error={errors.kunde_id?.message}
                {...register('kunde_id')}
              />
              <Select
                label="Modell" required
                options={GERAETE_MODELLE.map((m) => ({ value: m, label: m }))}
                error={errors.modell?.message}
                {...register('modell')}
              />
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Input label="Seriennummer" placeholder="DS4-2024-001" {...register('seriennummer')} />
                </div>
                <div className="pb-0.5">
                  <BarcodeScanner onScan={(val) => setValue('seriennummer', val)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Kaufdatum" type="date" {...register('kaufdatum')} />
                <Input label="Garantie bis" type="date" {...register('garantie_bis')} />
              </div>
              <Select
                label="Status"
                options={[{ value: 'aktiv', label: 'Aktiv' }, { value: 'defekt', label: 'Defekt' }]}
                {...register('status')}
              />
              <Select
                label="Abo aktiv"
                options={[{ value: '0', label: 'Nein – Kauf' }, { value: '1', label: 'Ja – Abo' }]}
                {...register('abo_aktiv')}
              />
              <Input label="Standort beim Kunden" placeholder="z.B. Küche Hauptbereich" {...register('standort')} />
              <Textarea label="Notizen" placeholder="Interne Notizen..." rows={3} {...register('notizen')} />
            </div>
          </Card>

          {mutation.error && <p className="text-sm text-red-600 text-center">{mutation.error.message}</p>}
          <Button type="submit" fullWidth size="lg" icon={<Save size={18} />} loading={mutation.isPending}>
            Gerät speichern
          </Button>
        </form>
      </div>
    </>
  )
}
